import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAccountant } from '../contexts/AccountantContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import LayoutPreviewButton from '../components/LayoutPreviewButton';
import LayoutThumbnailPicker from '../components/LayoutThumbnailPicker';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { User, Buildings, Bell, Palette, Briefcase, Crown, CheckCircle, Lock, ArrowRight, CircleNotch, FileText, ShieldCheck, Warning, Scales, Trash, Envelope, Bank, PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { LegalModal } from './LegalPages';
import ResetDataModal from '../components/ResetDataModal';
import EmailSettingsCard from '../components/EmailSettingsCard';

export default function SettingsPage() {
  const { user } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const { accountantMode, accountantSettings, toggleAccountantMode, updateAccountantSettings, branding, loading: accountantLoading } = useAccountant();
  const navigate = useNavigate();
  const [togglingMode, setTogglingMode] = useState(false);
  const [updatingPlan, setUpdatingPlan] = useState(false);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalModalType, setLegalModalType] = useState('terms');
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [bankDetails, setBankDetails] = useState({ bank_name: '', bank_account: '', bank_account_name: '', bank_ifsc: '', bank_iban: '', bank_swift: '', bank_branch: '', bank_routing_code: '', tax_id: '', website: '', default_prepared_by_name: '', default_prepared_by_designation: '', default_approved_by_name: '', default_approved_by_designation: '', prepared_by_signature_data: '', approved_by_signature_data: '', public_base_url: '', layout_template: 'classic', hide_documents_from_others: false });
  const [bankLoading, setBankLoading] = useState(false);
  const [bankSaving, setBankSaving] = useState(false);
  const [signSaving, setSignSaving] = useState(false);
  const [profile, setProfile] = useState({ first_name: '', last_name: '', phone: '', date_of_birth: '', secret_question: '', secret_answer: '' });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [secretQuestions, setSecretQuestions] = useState([]);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchBankDetails = async () => {
      setBankLoading(true);
      try {
        const token = localStorage.getItem('erp_token');
        const res = await fetch(`${API}/api/company/settings`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setBankDetails({
            bank_name: data.bank_name || '',
            bank_account: data.bank_account || '',
            bank_account_name: data.bank_account_name || '',
            bank_ifsc: data.bank_ifsc || '',
            bank_iban: data.bank_iban || '',
            bank_swift: data.bank_swift || '',
            bank_branch: data.bank_branch || '',
            bank_routing_code: data.bank_routing_code || '',
            tax_id: data.tax_id || '',
            website: data.website || '',
            default_prepared_by_name: data.default_prepared_by_name || '',
            default_prepared_by_designation: data.default_prepared_by_designation || '',
            default_approved_by_name: data.default_approved_by_name || '',
            default_approved_by_designation: data.default_approved_by_designation || '',
            prepared_by_signature_data: data.prepared_by_signature_data || '',
            approved_by_signature_data: data.approved_by_signature_data || '',
            public_base_url: data.public_base_url || '',
            layout_template: data.layout_template || 'classic',
            hide_documents_from_others: !!data.hide_documents_from_others,
            activities: Array.isArray(data.activities) ? data.activities.join(', ') : (data.activities || ''),
          });
        }
      } catch {}
      setBankLoading(false);
    };
    fetchBankDetails();
  }, [API]);

  useEffect(() => {
    const fetchProfile = async () => {
      setProfileLoading(true);
      try {
        const token = localStorage.getItem('erp_token');
        const res = await fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setProfile({
            first_name: data.first_name || '', last_name: data.last_name || '',
            phone: data.phone || '', date_of_birth: data.date_of_birth || '',
            secret_question: data.secret_question || '', secret_answer: '',
          });
        }
        const qRes = await fetch(`${API}/api/auth/secret-questions`);
        if (qRes.ok) { const d = await qRes.json(); setSecretQuestions(d.questions || []); }
      } catch {}
      setProfileLoading(false);
    };
    fetchProfile();
  }, [API]);

  const saveProfile = async () => {
    if (!profile.date_of_birth) { toast.error('Date of birth is mandatory'); return; }
    setProfileSaving(true);
    try {
      const token = localStorage.getItem('erp_token');
      const body = { ...profile };
      if (!body.secret_answer) delete body.secret_answer;
      const res = await fetch(`${API}/api/auth/profile`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) toast.success('Profile updated');
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Error saving profile'); }
    setProfileSaving(false);
  };

  const saveBankDetails = async () => {
    setBankSaving(true);
    try {
      const token = localStorage.getItem('erp_token');
      const res = await fetch(`${API}/api/company/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(bankDetails),
      });
      if (res.ok) toast.success('Bank details saved successfully');
      else toast.error('Failed to save bank details');
    } catch { toast.error('Error saving bank details'); }
    setBankSaving(false);
  };

  const openLegalModal = (type) => {
    setLegalModalType(type);
    setLegalModalOpen(true);
  };

  const handleToggleAccountantMode = async (enabled) => {
    setTogglingMode(true);
    try {
      await toggleAccountantMode(enabled);
      if (enabled) {
        toast.success('Welcome to BizCore Pro™! Redirecting to Accountant Dashboard...');
        setTimeout(() => navigate('/dashboard/accountant'), 1500);
      } else {
        toast.success('Switched back to BizCore™ SME mode');
      }
    } catch (error) {
      toast.error('Failed to toggle accountant mode');
    } finally {
      setTogglingMode(false);
    }
  };

  const handlePlanChange = async (plan) => {
    setUpdatingPlan(true);
    try {
      await updateAccountantSettings({ accountant_plan: plan });
      toast.success(`Upgraded to ${plan.replace('accountant_', '').toUpperCase()} plan`);
    } catch (error) {
      toast.error('Failed to update plan');
    } finally {
      setUpdatingPlan(false);
    }
  };

  const getPlanFeatures = (plan) => {
    const features = {
      accountant_basic: { maxClients: 10, bulk: false, advanced: false },
      accountant_pro: { maxClients: 25, bulk: true, advanced: true },
      accountant_enterprise: { maxClients: 'Unlimited', bulk: true, advanced: true }
    };
    return features[plan] || features.accountant_basic;
  };

  // Route-level guard: if the RBAC matrix has revoked settings.view for this
  // user's role, block the whole page (matches sidebar hide + prevents direct
  // URL access). Admin/Super Admin always pass regardless of matrix.
  const canViewSettings = ['admin', 'super_admin'].includes(user?.role)
    || !!user?.permissions?.settings?.includes('view');
  if (user && !canViewSettings) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center" data-testid="settings-denied">
        <Lock size={40} className="mx-auto mb-4 text-muted-foreground" />
        <h2 className="text-xl font-semibold mb-2">Access denied</h2>
        <p className="text-sm text-muted-foreground">
          Your role does not have permission to view the Settings page. Please
          contact your Company Admin if you need access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-3xl" data-testid="settings-page">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">
          Settings
        </h1>
        <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mt-1">
          Manage your account and application preferences
        </p>
      </motion.div>

      {/* Accountant Mode Section (BizCore Pro) */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className={`rounded-lg p-6 border-2 ${accountantMode ? 'bg-gradient-to-br from-[#0f172a] to-[#1e293b] text-white border-[#d4a84b]' : 'card-tactile border-transparent'}`}
      >
        <div className="flex items-center gap-3 mb-6">
          <div className={`p-2 rounded-lg ${accountantMode ? 'bg-[#d4a84b]/20' : 'bg-purple-500/10'}`}>
            <Briefcase size={20} className={accountantMode ? 'text-[#d4a84b]' : 'text-purple-600 dark:text-purple-400'} weight="duotone" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="font-heading text-lg font-medium">
                {accountantMode ? 'BizCore Pro™' : 'Accountant Mode'}
              </h2>
              {accountantMode && (
                <Badge className="bg-[#d4a84b] text-[#0f172a]">
                  <Crown size={12} className="mr-1" /> ACTIVE
                </Badge>
              )}
            </div>
            <p className={`text-sm ${accountantMode ? 'text-slate-300' : 'text-muted-foreground'}`}>
              {accountantMode 
                ? 'Multi-Client Business & Tax Compliance Platform' 
                : 'Manage multiple client companies from a single dashboard'}
            </p>
          </div>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10">
            <div>
              <p className="font-medium">Enable Accountant Mode</p>
              <p className={`text-sm ${accountantMode ? 'text-slate-400' : 'text-muted-foreground'}`}>
                Switch to multi-client management interface
              </p>
            </div>
            <Switch 
              checked={accountantMode}
              onCheckedChange={handleToggleAccountantMode}
              disabled={togglingMode}
              data-testid="accountant-mode-switch"
            />
          </div>

          {accountantMode && (
            <>
              <Separator className="bg-white/10" />
              
              {/* Plan Selection */}
              <div className="space-y-3">
                <Label className={`text-xs tracking-[0.05em] uppercase font-bold ${accountantMode ? 'text-slate-400' : 'text-muted-foreground'}`}>
                  Subscription Plan
                </Label>
                <div className="grid grid-cols-3 gap-3">
                  {['accountant_basic', 'accountant_pro', 'accountant_enterprise'].map((plan) => {
                    const isSelected = accountantSettings?.accountant_plan === plan;
                    const features = getPlanFeatures(plan);
                    return (
                      <div
                        key={plan}
                        onClick={() => !isSelected && handlePlanChange(plan)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected 
                            ? 'border-[#d4a84b] bg-[#d4a84b]/10' 
                            : 'border-white/10 hover:border-white/30'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <p className="font-semibold text-sm">{plan.replace('accountant_', '').toUpperCase()}</p>
                          {isSelected && <CheckCircle size={14} className="text-[#d4a84b]" weight="fill" />}
                        </div>
                        <p className="text-xs text-slate-400">Up to {features.maxClients} clients</p>
                        <div className="flex items-center gap-1 mt-1">
                          {features.bulk ? (
                            <CheckCircle size={10} className="text-emerald-400" />
                          ) : (
                            <Lock size={10} className="text-slate-500" />
                          )}
                          <span className="text-xs text-slate-500">Bulk ops</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Quick Access */}
              <Button 
                className="w-full bg-[#d4a84b] hover:bg-[#c49a43] text-[#0f172a]"
                onClick={() => navigate('/dashboard/accountant')}
              >
                Open Accountant Dashboard <ArrowRight size={16} className="ml-2" />
              </Button>
            </>
          )}
        </div>
      </motion.div>

      {/* Profile Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card-tactile rounded-lg p-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-primary/10">
            <User size={20} className="text-primary" weight="duotone" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-medium">Profile</h2>
            <p className="text-sm text-muted-foreground">Your personal information</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
              First Name
            </Label>
            <Input 
              value={user?.first_name || ''} 
              readOnly 
              className="bg-muted"
              data-testid="settings-firstname"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
              Last Name
            </Label>
            <Input 
              value={user?.last_name || ''} 
              readOnly 
              className="bg-muted"
              data-testid="settings-lastname"
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
              Email
            </Label>
            <Input 
              value={user?.email || ''} 
              readOnly 
              className="bg-muted"
              data-testid="settings-email"
            />
          </div>
        </div>
      </motion.div>

      {/* Company Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card-tactile rounded-lg p-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-blue-500/10">
            <Buildings size={20} className="text-blue-600 dark:text-blue-400" weight="duotone" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-medium">Company</h2>
            <p className="text-sm text-muted-foreground">Your organization details</p>
          </div>
        </div>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
              Company Name
            </Label>
            <Input 
              value={user?.company_name || 'Not set'} 
              readOnly 
              className="bg-muted"
              data-testid="settings-company"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
              Role
            </Label>
            <Input 
              value={user?.role || 'User'} 
              readOnly 
              className="bg-muted capitalize"
              data-testid="settings-role"
            />
          </div>
        </div>
      </motion.div>

      {/* Appearance Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card-tactile rounded-lg p-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-amber-500/10">
            <Palette size={20} className="text-amber-600 dark:text-amber-400" weight="duotone" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-medium">Appearance</h2>
            <p className="text-sm text-muted-foreground">Customize how the app looks</p>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">Dark Mode</p>
            <p className="text-sm text-muted-foreground">Toggle dark theme for the application</p>
          </div>
          <Switch 
            checked={isDark}
            onCheckedChange={toggleTheme}
            data-testid="dark-mode-switch"
          />
        </div>
      </motion.div>

      {/* Profile & Security Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.22 }}
        className="card-tactile rounded-lg p-6"
        data-testid="profile-security-settings"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-indigo-500/10">
            <ShieldCheck size={20} className="text-indigo-600 dark:text-indigo-400" weight="duotone" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-medium">Profile & Account Recovery</h2>
            <p className="text-sm text-muted-foreground">Set up your date of birth and security question for password recovery</p>
          </div>
        </div>
        {profileLoading ? (
          <div className="text-sm text-muted-foreground">Loading...</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">First Name</Label>
                <Input value={profile.first_name} onChange={e => setProfile(p => ({...p, first_name: e.target.value}))} data-testid="profile-first-name" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Last Name</Label>
                <Input value={profile.last_name} onChange={e => setProfile(p => ({...p, last_name: e.target.value}))} data-testid="profile-last-name" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Phone</Label>
                <Input value={profile.phone} onChange={e => setProfile(p => ({...p, phone: e.target.value}))} placeholder="+971 50 000 0000" data-testid="profile-phone" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Date of Birth <span className="text-red-500">*</span></Label>
                <Input type="date" value={profile.date_of_birth} onChange={e => setProfile(p => ({...p, date_of_birth: e.target.value}))} required data-testid="profile-dob" />
                {!profile.date_of_birth && <p className="text-xs text-red-500">Required for password recovery</p>}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Security Question <span className="text-red-500">*</span></Label>
                <select value={profile.secret_question} onChange={e => setProfile(p => ({...p, secret_question: e.target.value}))}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-testid="profile-secret-question">
                  <option value="">Select a security question...</option>
                  {secretQuestions.map(q => <option key={q} value={q}>{q}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Security Answer <span className="text-red-500">*</span></Label>
                <Input value={profile.secret_answer} onChange={e => setProfile(p => ({...p, secret_answer: e.target.value}))} 
                  placeholder="Enter your answer (case insensitive)" data-testid="profile-secret-answer" />
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={saveProfile} disabled={profileSaving} data-testid="save-profile-btn">
                {profileSaving ? <CircleNotch size={16} className="animate-spin mr-2" /> : null}
                {profileSaving ? 'Saving...' : 'Save Profile'}
              </Button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Email Configuration Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="card-tactile rounded-lg p-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-emerald-500/10">
            <Envelope size={20} className="text-emerald-600 dark:text-emerald-400" weight="duotone" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-medium">Email Integration</h2>
            <p className="text-sm text-muted-foreground">Configure email to send documents directly to suppliers</p>
          </div>
        </div>
        <EmailSettingsCard />
      </motion.div>

      {/* Bank Details Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.28 }}
        className="card-tactile rounded-lg p-6"
        data-testid="bank-details-settings"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-blue-500/10">
            <Bank size={20} className="text-blue-600 dark:text-blue-400" weight="duotone" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-medium">Bank Details</h2>
            <p className="text-sm text-muted-foreground">Bank information shown on invoices & quotations</p>
          </div>
        </div>
        {bankLoading ? (
          <div className="text-sm text-muted-foreground">Loading...</div>
        ) : (
          <div className="space-y-4">
            {/* Tax / Web details — printed on every invoice & quotation header */}
            <div className="rounded-md border bg-muted/40 p-3 space-y-3">
              <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">
                Tax & Online Identity (shown on invoices & quotations)
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                    TRN / Tax Registration No.
                  </Label>
                  <Input
                    value={bankDetails.tax_id || ''}
                    onChange={(e) => setBankDetails((p) => ({ ...p, tax_id: e.target.value }))}
                    placeholder="e.g. 100123456700003 (UAE TRN) or 22ABCDE1234F1Z5 (GSTIN)"
                    data-testid="company-tax-id-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                    Website
                  </Label>
                  <Input
                    value={bankDetails.website || ''}
                    onChange={(e) => setBankDetails((p) => ({ ...p, website: e.target.value }))}
                    placeholder="www.yourcompany.com"
                    data-testid="company-website-input"
                  />
                </div>
              </div>
            </div>
            <div className="rounded-md border bg-muted/40 p-3 space-y-3">
              <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">
                Business Activities (printed on every document header)
              </div>
              <p className="text-xs text-muted-foreground">
                Comma-separated list. Shows on Invoice, Quotation, LPO, GRN, Voucher, Payslip &amp; Report PDFs as a horizontal strip below the company logo. Adapts to the selected PDF layout automatically. Max 12 entries.
              </p>
              <Input
                value={bankDetails.activities || ''}
                onChange={(e) => setBankDetails((p) => ({ ...p, activities: e.target.value }))}
                placeholder="e.g. Project Management, Facilities Management, Maintenance, IT Services, Trading"
                data-testid="company-activities-input"
              />
              {bankDetails.activities ? (
                <div className="text-[11px] text-muted-foreground">
                  Preview: {bankDetails.activities.split(',').map(s => s.trim()).filter(Boolean).slice(0, 12).map((a, i, arr) => (
                    <span key={a + i}>
                      <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle" style={{ background: '#E07B39' }} />
                      <span className="font-medium text-foreground">{a}</span>
                      {i < arr.length - 1 && <span className="mx-2 text-muted-foreground">·</span>}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Bank Name</Label>
                <Input value={bankDetails.bank_name} onChange={e => setBankDetails(p => ({...p, bank_name: e.target.value}))} placeholder="e.g. Emirates NBD" data-testid="bank-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Branch</Label>
                <Input value={bankDetails.bank_branch} onChange={e => setBankDetails(p => ({...p, bank_branch: e.target.value}))} placeholder="e.g. Dubai Main Branch" data-testid="bank-branch-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Account Name</Label>
                <Input value={bankDetails.bank_account_name} onChange={e => setBankDetails(p => ({...p, bank_account_name: e.target.value}))} placeholder="e.g. VRV Technical FZ LLC" data-testid="bank-account-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Account Number</Label>
                <Input value={bankDetails.bank_account} onChange={e => setBankDetails(p => ({...p, bank_account: e.target.value}))} placeholder="Account number" data-testid="bank-account-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">IBAN</Label>
                <Input value={bankDetails.bank_iban} onChange={e => setBankDetails(p => ({...p, bank_iban: e.target.value}))} placeholder="e.g. AE07 0331 0000 0000 0000 00" data-testid="bank-iban-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">BIC / SWIFT Code</Label>
                <Input value={bankDetails.bank_swift} onChange={e => setBankDetails(p => ({...p, bank_swift: e.target.value}))} placeholder="e.g. EABORAED" data-testid="bank-swift-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Routing Code</Label>
                <Input value={bankDetails.bank_routing_code} onChange={e => setBankDetails(p => ({...p, bank_routing_code: e.target.value}))} placeholder="e.g. 302000" data-testid="bank-routing-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">IFSC Code</Label>
                <Input value={bankDetails.bank_ifsc} onChange={e => setBankDetails(p => ({...p, bank_ifsc: e.target.value}))} placeholder="For Indian banks" data-testid="bank-ifsc-input" />
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={saveBankDetails} disabled={bankSaving} data-testid="save-bank-details-btn">
                {bankSaving ? <CircleNotch size={16} className="animate-spin mr-2" /> : null}
                {bankSaving ? 'Saving...' : 'Save Bank Details'}
              </Button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Document Signatures Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.32 }}
        className="card-tactile rounded-lg p-6"
        data-testid="signatures-settings"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-amber-500/10">
            <PencilSimple size={20} className="text-amber-600 dark:text-amber-400" weight="duotone" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-medium">Document Signatures</h2>
            <p className="text-sm text-muted-foreground">Override "Prepared By" / "Approved By" names on ALL document PDFs (Invoice, Quotation, PO, RFQ, GRN, Vouchers, Reports). Leave blank to use the logged-in user's name.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Prepared By Name</label>
            <Input
              value={bankDetails.default_prepared_by_name}
              onChange={e => setBankDetails(p => ({ ...p, default_prepared_by_name: e.target.value }))}
              placeholder="e.g. TAYYAB AHMED"
              data-testid="setting-prepared-by-name"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Prepared By Designation</label>
            <Input
              value={bankDetails.default_prepared_by_designation}
              onChange={e => setBankDetails(p => ({ ...p, default_prepared_by_designation: e.target.value }))}
              placeholder="e.g. Accountant"
              data-testid="setting-prepared-by-designation"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Prepared By Signature (PNG)</label>
            <div className="flex items-center gap-3 mt-1">
              <label className="cursor-pointer inline-flex items-center gap-1 rounded-md border border-input bg-background px-3 py-1.5 text-xs hover:bg-accent">
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={async e => {
                    const f = e.target.files?.[0]; if (!f) return;
                    const reader = new FileReader();
                    reader.onload = () => setBankDetails(p => ({ ...p, prepared_by_signature_data: String(reader.result || '') }));
                    reader.readAsDataURL(f);
                    e.target.value = '';
                  }}
                  data-testid="setting-prepared-by-sig-input"
                />
                Upload Signature
              </label>
              {bankDetails.prepared_by_signature_data ? (
                <>
                  <img src={bankDetails.prepared_by_signature_data} alt="signature" className="h-10 max-w-[200px] object-contain bg-white border rounded" />
                  <Button variant="ghost" size="sm" onClick={() => setBankDetails(p => ({ ...p, prepared_by_signature_data: '' }))}>Remove</Button>
                </>
              ) : <span className="text-xs text-muted-foreground">No image uploaded</span>}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Approved By Name</label>
            <Input
              value={bankDetails.default_approved_by_name}
              onChange={e => setBankDetails(p => ({ ...p, default_approved_by_name: e.target.value }))}
              placeholder="e.g. SHAFAQAT ALI GHUMMAN"
              data-testid="setting-approved-by-name"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Approved By Designation</label>
            <Input
              value={bankDetails.default_approved_by_designation}
              onChange={e => setBankDetails(p => ({ ...p, default_approved_by_designation: e.target.value }))}
              placeholder="e.g. Managing Director"
              data-testid="setting-approved-by-designation"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Approved By Signature (PNG)</label>
            <div className="flex items-center gap-3 mt-1">
              <label className="cursor-pointer inline-flex items-center gap-1 rounded-md border border-input bg-background px-3 py-1.5 text-xs hover:bg-accent">
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={async e => {
                    const f = e.target.files?.[0]; if (!f) return;
                    const reader = new FileReader();
                    reader.onload = () => setBankDetails(p => ({ ...p, approved_by_signature_data: String(reader.result || '') }));
                    reader.readAsDataURL(f);
                    e.target.value = '';
                  }}
                  data-testid="setting-approved-by-sig-input"
                />
                Upload Signature
              </label>
              {bankDetails.approved_by_signature_data ? (
                <>
                  <img src={bankDetails.approved_by_signature_data} alt="signature" className="h-10 max-w-[200px] object-contain bg-white border rounded" />
                  <Button variant="ghost" size="sm" onClick={() => setBankDetails(p => ({ ...p, approved_by_signature_data: '' }))}>Remove</Button>
                </>
              ) : <span className="text-xs text-muted-foreground">No image uploaded</span>}
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Public Verification URL (for QR codes)</label>
            <Input
              value={bankDetails.public_base_url}
              onChange={e => setBankDetails(p => ({ ...p, public_base_url: e.target.value }))}
              placeholder="https://bizcore.sgkapps.com"
              data-testid="setting-public-base-url"
            />
            <p className="text-xs text-muted-foreground mt-1">QR codes on PDFs will encode <code>{`{this URL}/api/verify/...`}</code>. Set this to your production domain (e.g. <code>https://bizcore.sgkapps.com</code>) so scans always reach the live site, regardless of which environment generated the PDF.</p>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Default PDF Layout</label>
            <LayoutThumbnailPicker
              value={bankDetails.layout_template || 'classic'}
              onChange={(v) => setBankDetails(p => ({ ...p, layout_template: v }))}
            />
            <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <span>Click a thumbnail to apply. Selected: <strong className="text-foreground capitalize">{(bankDetails.layout_template || 'classic').replace('_', ' ')}</strong>.</span>
              <LayoutPreviewButton layout={bankDetails.layout_template || 'classic'} label="Open full-size preview" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Applies to Quotation, Invoice, RFQ, PO, GRN and Report PDFs. Individual documents can still override this in their create form.</p>
          </div>

          {/* Document Visibility across users — Company Admin only.
              Staff must never see this toggle: it controls THEIR visibility,
              not their own preference. Backend also rejects the field for
              non-admins as defence in depth. */}
          {['admin', 'super_admin'].includes(user?.role) && (
          <div className="md:col-span-2 border-t border-border pt-4">
            <label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Document Visibility</label>
            <div className="mt-2 flex items-start gap-3">
              <input
                type="checkbox"
                id="hide-docs-toggle"
                checked={!!bankDetails.hide_documents_from_others}
                onChange={e => setBankDetails(p => ({ ...p, hide_documents_from_others: e.target.checked }))}
                className="mt-1 h-4 w-4 rounded border-border accent-primary"
                data-testid="hide-docs-toggle"
              />
              <label htmlFor="hide-docs-toggle" className="text-sm cursor-pointer select-none">
                <div className="font-medium">Hide documents created by other users</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  When ON: each non-admin only sees Quotations / Invoices / POs / RFQs / Vouchers they personally created.
                  Company Admins &amp; Super Admins always see everything.
                </div>
              </label>
            </div>
          </div>
          )}
        </div>
        <div className="flex justify-end mt-4">
          <Button
            onClick={async () => {
              setSignSaving(true);
              try {
                const token = localStorage.getItem('erp_token');
                const res = await fetch(`${API}/api/company/settings`, {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({
                    default_prepared_by_name: bankDetails.default_prepared_by_name,
                    default_prepared_by_designation: bankDetails.default_prepared_by_designation,
                    default_approved_by_name: bankDetails.default_approved_by_name,
                    default_approved_by_designation: bankDetails.default_approved_by_designation,
                    prepared_by_signature_data: bankDetails.prepared_by_signature_data,
                    approved_by_signature_data: bankDetails.approved_by_signature_data,
                    public_base_url: bankDetails.public_base_url,
                    layout_template: bankDetails.layout_template,
                    hide_documents_from_others: !!bankDetails.hide_documents_from_others,
                  }),
                });
                if (res.ok) toast.success('Document signatures updated — applies to all new PDFs.');
                else toast.error('Failed to save signatures');
              } catch { toast.error('Failed to save signatures'); }
              setSignSaving(false);
            }}
            disabled={signSaving}
            data-testid="save-signatures-btn"
          >
            {signSaving ? <CircleNotch size={16} className="animate-spin mr-2" /> : null}
            {signSaving ? 'Saving...' : 'Save Document Signatures'}
          </Button>
        </div>
      </motion.div>

      {/* Notifications Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="card-tactile rounded-lg p-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-emerald-500/10">
            <Bell size={20} className="text-emerald-600 dark:text-emerald-400" weight="duotone" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-medium">Notifications</h2>
            <p className="text-sm text-muted-foreground">Manage your notification preferences</p>
          </div>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Email Notifications</p>
              <p className="text-sm text-muted-foreground">Receive updates via email</p>
            </div>
            <Switch defaultChecked data-testid="email-notifications-switch" />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Push Notifications</p>
              <p className="text-sm text-muted-foreground">Get browser notifications</p>
            </div>
            <Switch data-testid="push-notifications-switch" />
          </div>
        </div>
      </motion.div>

      {/* Legal & Policies Section */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="rounded-2xl border bg-card p-6 shadow-sm"
        data-testid="legal-settings"
      >
        <div className="flex items-center gap-4 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[#1e3a5f]/10 flex items-center justify-center">
            <Scales size={20} className="text-[#1e3a5f]" weight="duotone" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-medium">Legal & Policies</h2>
            <p className="text-sm text-muted-foreground">View our terms, privacy policy, and disclaimer</p>
          </div>
        </div>
        
        <div className="space-y-3">
          {/* Terms & Conditions */}
          <button 
            onClick={() => openLegalModal('terms')}
            className="w-full flex items-center justify-between p-4 rounded-xl border border-border hover:bg-accent/50 transition-colors group"
            data-testid="view-terms-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <FileText size={18} className="text-blue-600 dark:text-blue-400" weight="duotone" />
              </div>
              <div className="text-left">
                <p className="font-medium">Terms & Conditions</p>
                <p className="text-xs text-muted-foreground">Version 1.0 • Last updated March 27, 2025</p>
              </div>
            </div>
            <ArrowRight size={18} className="text-muted-foreground group-hover:text-foreground transition-colors" />
          </button>

          {/* Privacy Policy */}
          <button 
            onClick={() => openLegalModal('privacy')}
            className="w-full flex items-center justify-between p-4 rounded-xl border border-border hover:bg-accent/50 transition-colors group"
            data-testid="view-privacy-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <ShieldCheck size={18} className="text-green-600 dark:text-green-400" weight="duotone" />
              </div>
              <div className="text-left">
                <p className="font-medium">Privacy Policy</p>
                <p className="text-xs text-muted-foreground">Version 1.0 • Last updated March 27, 2025</p>
              </div>
            </div>
            <ArrowRight size={18} className="text-muted-foreground group-hover:text-foreground transition-colors" />
          </button>

          {/* Disclaimer */}
          <button 
            onClick={() => openLegalModal('disclaimer')}
            className="w-full flex items-center justify-between p-4 rounded-xl border border-border hover:bg-accent/50 transition-colors group"
            data-testid="view-disclaimer-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Warning size={18} className="text-amber-600 dark:text-amber-400" weight="duotone" />
              </div>
              <div className="text-left">
                <p className="font-medium">Disclaimer</p>
                <p className="text-xs text-muted-foreground">Version 1.0 • Last updated March 27, 2025</p>
              </div>
            </div>
            <ArrowRight size={18} className="text-muted-foreground group-hover:text-foreground transition-colors" />
          </button>
        </div>

        {/* Danger Zone - Only for Super Admin */}
        {user?.role === 'super_admin' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <Warning size={20} className="text-red-600" weight="fill" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-red-800 dark:text-red-400">Danger Zone</h3>
                <p className="text-sm text-red-600 dark:text-red-500">Irreversible actions</p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-white dark:bg-background border border-red-200 dark:border-red-900 rounded-lg">
                <div>
                  <p className="font-medium text-red-800 dark:text-red-400">Start Fresh / Reset All Data</p>
                  <p className="text-sm text-red-600 dark:text-red-500">
                    Delete all transactional data and start with a clean slate. Company settings and users will be preserved.
                  </p>
                </div>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => setResetModalOpen(true)}
                  data-testid="open-reset-modal"
                >
                  <Trash size={16} className="mr-2" />
                  Reset Data
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground text-center">
            By using BIZ<span className="italic">Core</span>™, you agree to our policies. For questions, contact{' '}
            <a href="mailto:info@sgkapps.com" className="text-[#d4a84b] hover:underline">info@sgkapps.com</a>
          </p>
        </div>
      </motion.div>

      {/* Legal Modal */}
      <LegalModal 
        isOpen={legalModalOpen} 
        onClose={() => setLegalModalOpen(false)} 
        type={legalModalType}
      />

      {/* Reset Data Modal */}
      <ResetDataModal
        isOpen={resetModalOpen}
        onClose={() => setResetModalOpen(false)}
        companyName={user?.company_name || 'Company'}
      />
    </div>
  );
}
